import { LinkOutOutline18Icon } from "@vygruppen/spor-icon-react";
import { getDriftsinfoBaseUrl } from "api/common";
import { format } from "date-fns";
import { EventHeaderStatus } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/EventHeaderStatus";
import { getStartEndData } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/durationDisplay";
import { AltTransIncidentMeasurePlanStatus } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/types";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { FC, type JSX } from "react";
import { useParams } from "react-router-dom";
import {
  Badge,
  DropsButton,
  HStack,
  Text,
  tokens,
} from "shared/components/Spor";
import { calendarDateTimeToDate } from "shared/utils/dateUtils";
import { Nullish } from "shared/utils/objectUtils";
import styled, { css } from "styled-components";

const HeaderWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
`;

const TitleRowWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
`;

const RowSectionWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${tokens.default.size.spacing[2]};
`;

const InfoRowWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(6, auto) 1fr;
  align-items: stretch;
  gap: ${tokens.default.size.spacing[2]} ${tokens.default.size.spacing[5]};
  padding-top: ${tokens.default.size.spacing[2]};

  @media (max-width: 1825px) {
    grid-template-columns: repeat(6, auto);
    & > :last-child {
      grid-column: 1 / -1;
      max-width: 900px;
    }
  }
`;

const InfoEntry = styled.div<{ $width?: string; $shrink?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  gap: ${tokens.default.size.spacing[0.5]};

  justify-content: flex-start;

  flex-basis: ${({ $width = "150px" }) => $width};

  ${({ $shrink }) =>
    $shrink
      ? css`
          flex-shrink: 1;
        `
      : ""}

  & > :last-child {
    flex-grow: 1;
  }
`;

type EventHeaderProps = {
  event: InfrastructureResponse;
  incidentPlanStatus: Nullish<AltTransIncidentMeasurePlanStatus>;
  incidentPlanLoadingState: "pending" | "error" | "success";
};

export const EventHeader: FC<EventHeaderProps> = ({
  event,
  incidentPlanStatus,
  incidentPlanLoadingState,
}) => {
  const { uuid } = useParams();

  const {
    start,
    end,
    openEndedMinutes,
    title: infoTypeText,
  } = getStartEndData(event.durations);

  const updatedAtDate = event.updatedAt
    ? calendarDateTimeToDate(event.updatedAt)
    : null;

  let endContent: JSX.Element | null = null;
  if (end) {
    endContent = (
      <>
        <Text variant="sm">{format(end, "dd.MM.yyyy")}</Text>
        <Text variant="sm">{format(end, "HH:mm")}</Text>
      </>
    );
  } else if (openEndedMinutes != null) {
    endContent = <Text variant="sm">{openEndedMinutes} minutter</Text>;
  }

  return (
    <HeaderWrapper>
      <TitleRowWrapper>
        <RowSectionWrapper>
          <Text variant="lg" fontWeight={700}>
            {event.title}
          </Text>
          <Text variant="lg">|</Text>
          <Text variant="lg">{event.stretchName}</Text>
          <HStack gap="1">
            <EventHeaderStatus
              incidentMeasurePlanStatus={incidentPlanStatus}
              incidentPlanLoadingState={incidentPlanLoadingState}
            />
            {event.state === "CLOSED" && (
              <Badge semantic="success">Friskmeldt</Badge>
            )}
          </HStack>
        </RowSectionWrapper>

        <RowSectionWrapper>
          <DropsButton
            rightIcon={<LinkOutOutline18Icon />}
            variant="tertiary"
            fontWeight="normal"
            size="xs"
            href={`${getDriftsinfoBaseUrl()}/information/${uuid}`}
            as="a"
            // @ts-ignore -- this is passed through to the anchor tag
            target="_blank"
          >
            Se hendelsesdetaljer
          </DropsButton>
        </RowSectionWrapper>
      </TitleRowWrapper>

      <InfoRowWrapper>
        <InfoEntry>
          <Text variant="sm" fontWeight="bold">
            Starttidspunkt
          </Text>
          <Text variant="sm">{start ? format(start, "dd.MM.yyyy") : "–"}</Text>
          <Text variant="sm">{start ? format(start, "HH:mm") : "–"}</Text>
        </InfoEntry>
        <InfoEntry>
          <Text variant="sm" fontWeight="bold">
            {infoTypeText}
          </Text>
          {endContent}
        </InfoEntry>
        <InfoEntry>
          <Text fontWeight="bold">Sist oppdatert</Text>
          <Text variant="sm">
            {updatedAtDate ? format(updatedAtDate, "dd.MM.yyyy") : "–"}
          </Text>
          <Text variant="sm">
            {updatedAtDate ? format(updatedAtDate, "HH:mm") : "–"}
          </Text>
        </InfoEntry>
        <InfoEntry>
          <Text fontWeight="bold">Aksjonskort</Text>
          <Text variant="sm">{event.actionCard?.type ?? "–"}</Text>
        </InfoEntry>
        {/* TODO: Add back in for Taxirevisjon */}
        {/* <InfoEntry>
          <Text fontWeight="bold">Referanse</Text>
          <div>–</div>
        </InfoEntry> */}
        <InfoEntry $shrink $width="500px">
          <Text fontWeight="bold">Siste melding</Text>
          <Text variant="sm">{event.message}</Text>
        </InfoEntry>
      </InfoRowWrapper>
    </HeaderWrapper>
  );
};
